import { useQueryClient } from '@tanstack/react-query';
import type { FC, ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { DebouncedState } from 'use-debounce';
import { useDebouncedCallback } from 'use-debounce';

import { MessageTypesE } from 'declarations/enums/messages';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { MessageI } from 'declarations/interfaces/messages';
import type { NoopT } from 'declarations/types/common';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import {
  useCreateTicketMessage,
  useCreateTicketScreenshotMessage,
  useFetchTicketMessages,
} from 'hooks/api/useTickets';

import type { NewTicketMessageFormValuesT } from '../components/MessageForm/declarations';

type UnsavedMessageT = Omit<MessageI, 'action' | 'attachmentName' | 'sender'> & {
  file?: Blob | null;
};

type MessagesContextT = {
  messages: MessageI[];
  isLoadingMessages: boolean;
  unsavedMessage: null | UnsavedMessageT;
  isCreatingMessage: boolean;
  isMessageCreationError: boolean;
  createMessageErrorMessage: string | undefined;
  createScreenshotMessageErrorMessage: string | undefined;
  createMessageValidationErrorMessage: string | undefined;
  createScreenShotMessageValidationErrorMessage: string | undefined;
  undoMessageTimeLeft: number;
  handleUndoMessage: NoopT;
  handleConfirmMessage: (assignToWarrCloud: boolean) => void;
  handleCreateUnsavedMessage: (
    data: NewTicketMessageFormValuesT,
    assignToWarrCloud: boolean
  ) => void;
  debouncedCreateTicketMessage: DebouncedState<(data: NewTicketMessageFormValuesT) => void>;
};

type MessagesProviderPropsT = {
  ticketId: string;
  children: ReactNode;
};

const contextDefaultValue = {
  messages: [],
  isLoadingMessages: false,
  unsavedMessage: null,
  isCreatingMessage: false,
  isMessageCreationError: false,
  createMessageErrorMessage: undefined,
  createMessageValidationErrorMessage: undefined,
  createScreenshotMessageErrorMessage: undefined,
  createScreenShotMessageValidationErrorMessage: undefined,
  undoMessageTimeLeft: 0,
  handleUndoMessage: (): undefined => undefined,
  handleConfirmMessage: (): undefined => undefined,
  handleCreateUnsavedMessage: (): undefined => undefined,
  debouncedCreateTicketMessage: Object.assign((): void => {}, {
    cancel: (): undefined => undefined,
    flush: (): undefined => undefined,
    isPending: () => false,
  }),
};

export const MessagesContext = createContext<MessagesContextT>(contextDefaultValue);

const saveMessageDelay = 30000;

const MessagesProvider: FC<MessagesProviderPropsT> = ({ ticketId, children }) => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const [unsavedMessage, setUnsavedMessage] = useState<null | UnsavedMessageT>(null);
  const [undoMessageTimeLeft, setUndoMessageTimeLeft] = useState(0);

  /**
   * Api hooks
   */
  const messagesResult = useFetchTicketMessages({
    ticketId,
  });
  const createTicketMessageMutation = useCreateTicketMessage({
    ticketId,
    onSuccess: (_data, variables, _context) => {
      setUnsavedMessage(null);
      setUndoMessageTimeLeft(0);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);

      if (variables.assignToOppositeUser) {
        queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
        queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      }
    },
  });

  const createTicketScreenshotMessageMutation = useCreateTicketScreenshotMessage({
    ticketId,
    onSuccess: (_data, variables, _context) => {
      setUnsavedMessage(null);
      setUndoMessageTimeLeft(0);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);

      if (variables.assignToOppositeUser) {
        queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
        queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      }
    },
  });

  const debouncedCreateTicketMessage = useDebouncedCallback(
    (data: NewTicketMessageFormValuesT, assignToWarrCloud: boolean) => {
      if (data.file) {
        createTicketScreenshotMessageMutation.mutate({
          text: data.message,
          assignToOppositeUser: assignToWarrCloud,
          file: data.file,
        });
      } else {
        createTicketMessageMutation.mutate({
          text: data.message,
          assignToOppositeUser: assignToWarrCloud,
        });
      }
    },
    saveMessageDelay
  );

  /**
   * Variables
   */
  const createMessageErrorMessage = useMemo(
    () => getErrorMessage(createTicketMessageMutation.error),
    [createTicketMessageMutation.error]
  );
  const createMessageValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(createTicketMessageMutation.error),
    [createTicketMessageMutation.error]
  );
  const createScreenshotMessageErrorMessage = useMemo(
    () => getErrorMessage(createTicketScreenshotMessageMutation.error),
    [createTicketScreenshotMessageMutation.error]
  );
  const createScreenShotMessageValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(createTicketScreenshotMessageMutation.error),
    [createTicketScreenshotMessageMutation.error]
  );

  /**
   * Handlers
   */
  const handleCreateUnsavedMessage = useCallback(
    (data: NewTicketMessageFormValuesT, assignToWarrCloud: boolean): void => {
      if (unsavedMessage) {
        debouncedCreateTicketMessage.flush();
      }

      setUnsavedMessage({
        text: data.message,
        typeId: MessageTypesE.Text,
        sentDate: new Date(),
        file: data.file ?? null,
      });
      setUndoMessageTimeLeft(saveMessageDelay / 1000);
      debouncedCreateTicketMessage(data, assignToWarrCloud);
    },
    [unsavedMessage, debouncedCreateTicketMessage]
  );
  const handleUndoMessage = useCallback((): void => {
    setUnsavedMessage(null);
    setUndoMessageTimeLeft(0);
    debouncedCreateTicketMessage.cancel();
  }, [debouncedCreateTicketMessage]);
  const handleConfirmMessage = useCallback(
    (_assignToWarrCloud: boolean): void => {
      debouncedCreateTicketMessage.flush();
    },
    [debouncedCreateTicketMessage]
  );
  const createInterval = useCallback(
    (): ReturnType<typeof setInterval> =>
      setInterval((): void => {
        setUndoMessageTimeLeft(undoMessageTimeLeft - 1);
      }, 950),
    [undoMessageTimeLeft]
  );

  /**
   * Effects
   */
  useEffect((): (() => void) | undefined => {
    if (!undoMessageTimeLeft) return;

    let intervalId: ReturnType<typeof setTimeout>;

    if (undoMessageTimeLeft) {
      intervalId = createInterval();
    }

    return () => clearInterval(intervalId);
  }, [undoMessageTimeLeft, createInterval]);

  return (
    <MessagesContext.Provider
      value={{
        messages: messagesResult.data ?? [],
        isLoadingMessages: messagesResult.isLoading || messagesResult.isRefetching,
        unsavedMessage,
        isMessageCreationError:
          createTicketMessageMutation.isError || createTicketScreenshotMessageMutation.isError,
        createMessageErrorMessage,
        createMessageValidationErrorMessage,
        createScreenshotMessageErrorMessage,
        createScreenShotMessageValidationErrorMessage,
        isCreatingMessage:
          createTicketMessageMutation.isLoading || createTicketScreenshotMessageMutation.isLoading,
        undoMessageTimeLeft,
        handleCreateUnsavedMessage,
        handleUndoMessage,
        handleConfirmMessage,
        debouncedCreateTicketMessage,
      }}
    >
      {children}
    </MessagesContext.Provider>
  );
};

const useMessages = (): MessagesContextT => useContext(MessagesContext);

export { MessagesProvider, useMessages };
